import { useState, useCallback, useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Typography, Paper, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import color from 'color';
import { snapToArray } from '../../utils';
import { useFirestore } from '../../utils/firebase';
import { VacationRequestFormContainer } from './VacationRequestForm';
import { HardwareRequestsFormContainer } from './HardwareRequestForm';
import { OutOfOfficeFormContainer } from './OutOfOfficeForm';
import { VacationTile } from './VacationTile';
import { BillableDaysTile } from './BillableDaysTile';
import { EduBudgetTile } from './EduBudgetTile';
import { EduBudgetRequestFormContainer } from './EduBudgetRequestForm';
import { useAuth } from '../../auth';

const DashboardView = () => {
  const theme = useTheme();
  const [requestForm, setRequestForm] = useState('vacation');
  const database = useFirestore();
  const { user } = useAuth();
  const [isLoading, setLoading] = useState(false);
  const [eduBudgetRequests, setEduBudgetRequests] = useState([]);
  const [preliminaryEduBudget, setPreliminaryEduBudget] = useState(0);

  const userAvailableEduBudget = user.availableEduBudget - preliminaryEduBudget;

  const handleRequestForm = event => {
    setRequestForm(event.target.value);
  };

  const loadRequestsWithPreliminaryAmount = useCallback(async () => {
    const pendingQueries = query(
      collection(database, 'edu_budget_requests'),
      where('userId', '==', user.uid),
      where('status', '==', 'pending'),
      orderBy('requestedAt', 'desc'),
    );

    const approvedQueries = query(
      collection(database, 'edu_budget_requests'),
      where('userId', '==', user.uid),
      where('status', '==', 'approved'),
      orderBy('requestedAt', 'desc'),
    );

    try {
      const [pendingQueriesSnapshot, approvedQueriesSnapshot] = await Promise.all([
        getDocs(pendingQueries),
        getDocs(approvedQueries),
      ]);

      const pendingRequestsArray = snapToArray(pendingQueriesSnapshot);
      const approvedRequestsArray = snapToArray(approvedQueriesSnapshot);

      const result = pendingRequestsArray.concat(approvedRequestsArray);

      setEduBudgetRequests(result);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [database, user.uid]);

  useEffect(() => {
    setLoading(true);
    loadRequestsWithPreliminaryAmount();
  }, [loadRequestsWithPreliminaryAmount]);

  useEffect(() => {
    setPreliminaryEduBudget(
      isLoading
        ? 0
        : eduBudgetRequests.reduce((accumulator, item) => accumulator + item.totalAmount, 0),
    );
  }, [eduBudgetRequests, isLoading]);

  return (
    <div style={{ padding: theme.spacing(4) }}>
      <Grid container spacing={4}>
        <Grid
          sx={{
            '@media (max-width:1279px)': {
              order: 1,
            },
          }}
          size={{
            lg: 4,
            md: 12,
            sm: 12,
            xs: 12,
          }}
        >
          <Paper
            sx={{
              padding: '1.5rem 1.5rem',
              display: 'flex',
              flexDirection: 'column',
              background: theme.palette.primary.main,
              borderRadius: '0.25rem 0.25rem 0px 0px',
            }}
            elevation={1}
          >
            <Typography
              variant="body2"
              component="span"
              sx={{
                color: '#fff',
                fontWeight: 600,
                marginBottom: theme.spacing(2),
                fontSize: '1rem',
              }}
            >
              Create New Request
            </Typography>
            <FormControl
              variant="standard"
              sx={{
                minWidth: 120,
                background: color.blue,
              }}
            >
              <InputLabel
                id="select-standard-label"
                sx={{
                  color: '#fff',
                  '&.Mui-focused': {
                    color: '#fff',
                  },
                }}
              >
                Request Type
              </InputLabel>
              <Select
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                }}
                labelId="select-standard-label"
                sx={{
                  color: '#fff',
                  '&.Mui-hover': {
                    color: '#fff',
                  },
                  '&:before': {
                    borderColor: '#fff',
                  },
                  '&:after': {
                    borderColor: '#fff',
                  },
                  '&:not(.Mui-disabled):hover::before': {
                    borderColor: '#fff',
                  },
                  '& .MuiSelect-icon': {
                    fill: '#fff',
                  },
                }}
                id="select-standard"
                value={requestForm}
                onChange={handleRequestForm}
              >
                <MenuItem value="vacation" name="vacation">
                  Vacation
                </MenuItem>
                <MenuItem value="outofoffice" name="outofoffice">
                  Out of Office
                </MenuItem>
                <MenuItem value="hardware" name="hardware">
                  Hardware
                </MenuItem>
                <MenuItem value="edubudget" name="edubudget">
                  Education Budget
                </MenuItem>
              </Select>
            </FormControl>
          </Paper>
          {(() => {
            if (requestForm === 'vacation') {
              return <VacationRequestFormContainer />;
            }
            if (requestForm === 'outofoffice') {
              return <OutOfOfficeFormContainer />;
            }
            if (requestForm === 'hardware') {
              return <HardwareRequestsFormContainer />;
            }
            if (requestForm === 'edubudget') {
              return (
                <EduBudgetRequestFormContainer
                  userAvailableEduBudget={userAvailableEduBudget}
                  preliminaryEduBudget={preliminaryEduBudget}
                  setPreliminaryEduBudget={setPreliminaryEduBudget}
                />
              );
            }
            return <VacationRequestFormContainer />;
          })()}
        </Grid>
        <Grid
          tem
          size={{
            lg: 8,
            md: 12,
            sm: 12,
            xs: 12,
          }}
        >
          <Grid container spacing={4}>
            <Grid
              size={{
                lg: 6,
                md: 12,
                sm: 12,
                xs: 12,
              }}
            >
              <VacationTile />
            </Grid>
            <Grid
              size={{
                lg: 6,
                md: 12,
                sm: 12,
                xs: 12,
              }}
            >
              <BillableDaysTile />
            </Grid>
            <Grid
              size={{
                lg: 6,
                md: 12,
                sm: 12,
                xs: 12,
              }}
            >
              <EduBudgetTile
                preliminaryEduBudget={preliminaryEduBudget}
                userAvailableEduBudget={userAvailableEduBudget}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default DashboardView;
